import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";
import dayjs from "dayjs";

// use local CSS spinner fallback instead of Chakra Spinner to avoid runtime dependency issues
// ===== API hooks =====
import { useLifePolicyEntryList, useDeleteLifePolicyEntryMutation, useBulkDeleteLifePolicyEntryMutation } from "../../../../services/redux/api/components";

const LifePolicyList = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  // ===== Mutation hooks =====
  const [deleteLifePolicy, { isLoading: isDeletingSingle }] = useDeleteLifePolicyEntryMutation();
  const [bulkDeleteLifePolicy, { isLoading: isDeletingBulk }] = useBulkDeleteLifePolicyEntryMutation();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter and Sort states
  const [filterColumn, setFilterColumn] = useState("LifeAssured");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // ===== API QUERY USAGE =====
  const { data: policies, isLoading, error, refetch } = useLifePolicyEntryList({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    searchTerm: isFilterApplied ? filterColumn : null,
    searchValue: isFilterApplied ? filterValue : null,
  });

  // Local state used while API is not ready - keep it in sync with API when active
  const [policiesState, setPoliciesState] = useState({ data: policies?.data ?? [], totalCount: policies?.totalCount ?? 0 });

  useEffect(() => {
    // keep local mock state in sync with remote when API returns
    if (policies?.data) {
      setPoliciesState({ data: policies.data, totalCount: policies.totalCount ?? policies.data.length });
    }
  }, [policies]);

  useEffect(() => {
    const options = [
      { value: "lifeAssured", label: "Life Assured" },
      { value: "PolicyNumber", label: "Policy Number" },
      { value: "Age", label: "Age" },
      { value: "DOB", label: "DOB" },
      { value: "SumAssured", label: "Sum Assured" },
      { value: "insurer", label: "Insurer" },
      { value: "product", label: "Product" },
      { value: "agency", label: "Agency" },
    ];
    setColumnOptions(options);
  }, []);

  // Helper: extract canonical id from a data row / node
  const extractId = (d) => {
    if (!d) return null;
    return d.PolicyId ?? d.PolicyID ?? d.PolicyID ?? d.id ?? d.Id ?? null;
  };

  // Normalize rowData so each row definitely has PolicyId and id (both set to same canonical value)
  const rowData = useMemo(() => {
    const source = policies?.data ?? policiesState.data ?? [];
    if (!Array.isArray(source)) return [];
    return source.map((item) => {
      const canonical = extractId(item) ?? null;
      return {
        ...item,
        PolicyId: canonical,
        id: canonical,
      };
    });
  }, [policies, policiesState]);

  const handleFilter = () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : sortDirection === "desc" ? null : "asc";
    }
    setSortField(direction ? field : null);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return () => {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  const [columnDefs, setColumnDefs] = useState([]);

  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Life Assured",
        field: "LifeAssured",
        flex: 1,
        headerComponent: createHeaderComponent("LifeAssured", "Life Assured"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const data = params?.data || {};
          // prefer explicit name fields if present, fall back to other possible keys
          return (
            <span className="text-sm">
              {data?.LifeAssuredName || data?.LifeAssured || data?.LifeAssuredId || data?.CustomerName || String(params.value || "")}
            </span>
          );
        },
      },
      {
        headerName: "DOB",
        field: "DOB",
        width: 120,
        headerComponent: createHeaderComponent("DOB", "DOB"),
        suppressMenu: true,
        cellRenderer: (params) => {
          if (!params.value) return null;

          const formatted = dayjs(params.value).format("DD MMM YYYY");
          // Example → "20 Jan 2025"

          return <span className="text-sm">{formatted}</span>;
        }
      },
      {
        headerName: "Age",
        field: "Age",
        width: 80,
        headerComponent: createHeaderComponent("Age", "Age"),
        suppressMenu: true,
      },
      {
        headerName: "Policy Number",
        field: "PolicyNumber",
        flex: 1,
        headerComponent: createHeaderComponent("PolicyNumber", "Policy Number"),
        suppressMenu: true,
      },
      {
        headerName: "Insurer",
        field: "InsurerId",
        flex: 1,
        headerComponent: createHeaderComponent("InsurerId", "Insurer"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const data = params?.data || {};
          return (
            <span className="text-sm">
              {data?.InsurerName || data?.Insurer || data?.InsurerId || String(params.value || "")}
            </span>
          );
        },
      },
      {
        headerName: "Product",
        field: "ProductName",
        flex: 1,
        headerComponent: createHeaderComponent("ProductName", "Product"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const data = params?.data || {};
          return (
            <span className="text-sm">
              {data?.ProductName || data?.Product || String(params.value || "")}
            </span>
          );
        },
      },
      {
        headerName: "Sum Assured",
        field: "SumAssured",
        width: 140,
        headerComponent: createHeaderComponent("SumAssured", "Sum Assured"),
        suppressMenu: true,
      },
      {
        headerName: "Agency",
        field: "Agency",
        flex: 1,
        headerComponent: createHeaderComponent("Agency", "Agency"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const data = params?.data || {};
          return (
            <span className="text-sm">{data?.AgencyName || data?.Agency || data?.AgencyId || String(params.value || "")}</span>
          );
        },
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        width: 120,
        cellRenderer: (params) => {
          const rowId = extractId(params.data);
          return (
            <div className="flex space-x-2 mt-1">
              <button
                className="edit-button"
                onClick={() => navigate(`/life-policy-entry/edit/${rowId}`)}
                title="Edit"
                onMouseDown={(e) => e.preventDefault()}
              >
                <SquarePen color="#787474" size={18} className="mt-1" />
              </button>
              <button
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  // use the same confirm modal flow — set up for single delete
                  setDeleteMode("single");
                  setDeleteId(rowId);
                  // populate selectedIdsRef so modal shows correct names (optional)
                  selectedIdsRef.current = [rowId].filter(Boolean);
                  setSelectedNames([params.data?.LifeAssuredName || params.data?.LifeAssured || ""]);
                  setPreserveSelection(false);
                  setIsOpen(true);
                }}
                title="Delete"
                onMouseDown={(e) => e.preventDefault()}
              >
                <Trash2 color="#787474" size={18} className="mt-1" />
              </button>
            </div>
          );
        },
      },
    ];

    setColumnDefs(createColumnDefs());
  }, [sortField, sortDirection, navigate]);

  // helper to extract selected ids and names
  const extractIdFromNode = (node) => {
    const d = node?.data ?? {};
    return d.PolicyId ?? d.PolicyID ?? d.id ?? d.Id ?? null;
  };

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
    const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

    selectedIdsRef.current = selectedNodes.map((node) => extractIdFromNode(node)).filter(Boolean);
    setSelectedNames(selectedNodes.map((node) => node.data?.LifeAssuredName || node.data?.LifeAssured || ""));
    setAllSelected(selectedIdsRef.current.length === totalDisplayedRows && totalDisplayedRows > 0);
    setDeleteMode(selectedIdsRef.current.length > 1 || allSelected ? "multi" : "single");
  };

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) {
      toast.warning("Please select at least one policy to delete");
      return;
    }

    setDeleteMode(allSelected || count > 1 ? "multi" : "single");
    setDeleteId(allSelected ? null : selectedIdsRef.current[0]);
    onOpen();
  };

  const onOpen = () => {
    const selectedNodes = gridRef.current?.api ? gridRef.current.api.getSelectedNodes() : [];
    selectedIdsRef.current = selectedNodes.map((node) => extractIdFromNode(node)).filter(Boolean);
    setSelectedNames(selectedNodes.map((node) => node.data?.LifeAssuredName || node.data?.LifeAssured || ""));
    setPreserveSelection(true);
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
    setDeleteId(null);
    selectedIdsRef.current = [];
    setSelectedNames([]);
  };

  // Immediate single-row delete handler — uses API but still uses confirm modal flow above.
  // If you prefer immediate confirmation via window.confirm, you can still use handleSingleDelete.
  const handleSingleDelete = async (id) => {
    if (!id) {
      toast.warning("Invalid policy id");
      return;
    }

    const ok = window.confirm("Are you sure you want to delete this policy?");
    if (!ok) return;

    try {
      const response = await deleteLifePolicy(id).unwrap();
      if (response?.success || response?.Status === "SUCCESS") {
        toast.success(response.message || response.Message || "Policy deleted successfully");
        if (typeof refetch === "function") refetch();
      } else {
        toast.error(response.message || response.Message || "Failed to delete policy");
      }
    } catch (err) {
      // fallback mock deletion for local preview
      setPoliciesState((prev) => ({
        data: prev.data ? prev.data.filter((p) => (extractId(p) !== id)) : [],
        totalCount: prev.totalCount ? prev.totalCount - 1 : 0,
      }));
      toast.success("Policy deleted (mock)");
    }
  };

  // Confirm modal action (handles both single and bulk)
  const confirmDelete = async () => {
    try {
      if (deleteMode === "single" && deleteId) {
        try {
          const response = await deleteLifePolicy(deleteId).unwrap();
          if (response?.success || response?.Status === "SUCCESS") {
            toast.success(response.message || response.Message || "Policy deleted successfully");
            if (typeof refetch === "function") refetch();
          } else {
            toast.error(response.message || response.Message || "Failed to delete policy");
          }
        } catch (apiErr) {
          // fallback to local mock deletion
          setPoliciesState((prev) => ({
            data: prev.data ? prev.data.filter((p) => extractId(p) !== deleteId) : [],
            totalCount: prev.totalCount ? prev.totalCount - 1 : 0,
          }));
          toast.success("Policy deleted (mock)");
        }
      } else {
        // Bulk delete
        const idsToDelete = allSelected
          ? rowData.map((x) => extractId(x)).filter(Boolean)
          : (Array.isArray(selectedIdsRef.current) ? selectedIdsRef.current : []);

        if (!idsToDelete || idsToDelete.length === 0) {
          toast.warning("No policies selected for deletion");
          onClose();
          return;
        }

        try {
          const response = await bulkDeleteLifePolicy({ ids: idsToDelete }).unwrap();
          if (response?.success || response?.Status === "SUCCESS") {
            toast.success(response.message || response.Message || `${idsToDelete.length} policy(ies) deleted successfully`);
            if (typeof refetch === "function") refetch();
          } else {
            toast.error(response.message || response.Message || "Failed to delete policies");
          }
        } catch (apiErr) {
          // fallback: apply mock deletion locally and notify user
          setPoliciesState((prev) => ({
            data: prev.data ? prev.data.filter((p) => !idsToDelete.includes(extractId(p))) : [],
            totalCount: prev.totalCount ? prev.totalCount - idsToDelete.length : 0,
          }));
          toast.success(`${idsToDelete.length} policy(ies) deleted (mock)`);
        }
      }
    } catch (err) {
      toast.error("Failed to delete policy");
    } finally {
      onClose();
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setItemsPerPage(newPageSize);
    setCurrentPage(1);
  };

  useEffect(() => {
    const totalPages = Math.ceil((policies?.totalCount ?? policiesState.totalCount ?? 0) / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [policies?.totalCount, policiesState.totalCount, currentPage, itemsPerPage]);

  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      gridRef.current.api.forEachNode((node) => {
        const nodeId = extractId(node.data);
        if (selectedIdsRef.current.includes(nodeId)) {
          node.setSelected(true);
        }
      });
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Life Policy Entries</h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 addListTable-button"
            onClick={() => navigate("/life-policy-entry/add")}
          >
            Add
          </button>
          {selectedNames.length > 0 && (
            <button
              className="px-4 py-2 addListTable-button"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={policies?.totalCount ?? policiesState.totalCount ?? 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} policies on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected policies?`
              : "Are you sure you want to delete this policy?"
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default LifePolicyList;